import { useTranslation } from "react-i18next";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import {
  useGetCompanyTimeAttendanceStatsQuery,
  useGetTimeAttendanceOverviewQuery,
} from "@/services/(company)/api.time-attendance";
import { Skeleton } from "@/components/ui/skeleton";
import { MetricRing, MiniStat, Sparkbars, WidgetNoAccess } from "./primitives";

/**
 * Today's attendance, at four densities.
 *
 * The weekly series is only fetched from `lg` up, where there is actually room to
 * plot it — the smaller sizes would download a series they cannot draw.
 */
export const AttendanceTodayWidget = ({ size }: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  const { data, isLoading, error } = useGetCompanyTimeAttendanceStatsQuery({});
  const isWide = size === "lg" || size === "xl" || size === "grand";
  const {
    data: overview,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = useGetTimeAttendanceOverviewQuery({ bucket: "week" }, { skip: !isWide });

  // Every figure here comes from the attendance stats, so losing them leaves
  // nothing to draw.
  if (error) return <WidgetNoAccess error={error} />;

  // The weekly series is a separate endpoint and only the wide sizes plot it —
  // losing it costs the chart panel, not the widget.
  const noSeries = Boolean(overviewError);
  const stats = data?.data;
  const total = stats?.totalEmployees ?? 0;
  const present = stats?.totalPresent ?? 0;
  const presentPercent = total > 0 ? (present / total) * 100 : 0;

  const series = (overview?.data?.labels ?? []).map((label, index) => ({
    label,
    value: overview?.data?.datasets?.present?.[index] ?? 0,
  }));

  // A single row, same width as `tall` without the second row `tall` stands
  // it up into — so it gets only the hero half: ring plus the one figure that
  // matters. A 78px ring and five stats (the sizes below it) turned this into
  // the widest overflow on the canvas; there is no room here for a breakdown,
  // only for the headline. The ring itself is capped at 38: a one-row card's
  // body is ~42px of actual content height once the shared header and padding
  // take theirs, and 54 (like `md`'s old 62) spilled past the card's own
  // bottom edge rather than merely crowding the row.
  if (size === "sm") {
    return (
      <div className="flex h-full items-center gap-3">
        <MetricRing
          percent={presentPercent}
          label={t("company:dashboard.present")}
          tone="success"
          size={38}
          isLoading={isLoading}
        />
        <div className="min-w-0">
          {isLoading ? (
            <Skeleton className="h-[18px] w-12 rounded" />
          ) : (
            <p className="text-sm font-semibold tabular-nums text-foreground">
              {present}
              <span className="text-muted-foreground">/{total}</span>
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            {t("company:widgets.checked_in")}
          </p>
        </div>
      </div>
    );
  }

  // Same width as `sm`, stood up: the ring keeps `sm`'s size and position, and
  // the extra row goes to the breakdown the wide sizes lay out next to it
  // instead of underneath it.
  if (size === "tall") {
    return (
      <div className="flex h-full flex-col justify-between gap-2">
        <div className="flex items-center gap-3">
          <MetricRing
            percent={presentPercent}
            label={t("company:dashboard.present")}
            tone="success"
            size={54}
            isLoading={isLoading}
          />
          <div className="min-w-0">
            {isLoading ? (
              <Skeleton className="h-[18px] w-12 rounded" />
            ) : (
              <p className="text-sm font-semibold tabular-nums text-foreground">
                {present}
                <span className="text-muted-foreground">/{total}</span>
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              {t("company:widgets.checked_in")}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 border-t border-border pt-2">
          <MiniStat
            label={t("company:dashboard.on_leave")}
            tone="info"
            value={stats?.totalOnLeave ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:dashboard.absent")}
            tone="danger"
            value={stats?.totalAbsent ?? 0}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      <div className="flex shrink-0 items-center">
        <MetricRing
          percent={presentPercent}
          label={t("company:dashboard.present")}
          caption={`${present}/${total}`}
          tone="success"
          // `md` is one row tall — same 38px cap as `sm`, for the same reason:
          // anything bigger clears the body's own ~42px of content height.
          size={size === "md" ? 38 : size === "grand" ? 92 : 78}
          isLoading={isLoading}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="grid grid-cols-3 gap-2">
          <MiniStat
            label={t("company:dashboard.present")}
            tone="success"
            value={present}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:dashboard.on_leave")}
            tone="info"
            value={stats?.totalOnLeave ?? 0}
            isLoading={isLoading}
          />
          <MiniStat
            label={t("company:dashboard.absent")}
            tone="danger"
            value={stats?.totalAbsent ?? 0}
            isLoading={isLoading}
          />
        </div>

        {size !== "md" && (
          <div className="grid grid-cols-2 gap-2 border-t border-border pt-2">
            <MiniStat
              label={t("company:widgets.late_check_in")}
              tone="warning"
              value={stats?.totalLateCheckIn ?? 0}
              isLoading={isLoading}
            />
            <MiniStat
              label={t("company:widgets.early_check_out")}
              tone="warning"
              value={stats?.totalEarlyCheckOut ?? 0}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>

      {isWide && (
        <div className="flex min-w-0 flex-1 flex-col border-s border-border ps-4">
          <p className="mb-1.5 shrink-0 text-[10px] font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            {t("company:widgets.present_this_week")}
          </p>
          <div className="min-h-0 flex-1">
            {noSeries ? (
              <WidgetNoAccess
                error={overviewError}
                message={t("company:widgets.no_access_to_weekly_series")}
              />
            ) : (
              <Sparkbars
                series={series}
                tone="success"
                isLoading={isOverviewLoading}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
